<template>
  <div class="feedback-page">
    <section class="feedback-toolbar" aria-label="问题反馈概览">
      <div class="feedback-metrics" aria-label="反馈数据概览">
        <div class="feedback-metric feedback-metric--pending">
          <span>待处理</span>
          <strong>{{ stats.pending }}</strong>
        </div>
        <div class="feedback-metric">
          <span>处理中</span>
          <strong>{{ stats.processing }}</strong>
        </div>
        <div class="feedback-metric">
          <span>已处理</span>
          <strong>{{ stats.resolved }}</strong>
        </div>
        <div class="feedback-metric">
          <span>已忽略</span>
          <strong>{{ stats.ignored }}</strong>
        </div>
        <div class="feedback-metric">
          <span>今日提交</span>
          <strong>{{ stats.todaySubmitted }}</strong>
        </div>
        <div class="feedback-metric">
          <span>全部</span>
          <strong>{{ stats.total }}</strong>
        </div>
      </div>
    </section>

    <section class="feedback-filters" aria-label="反馈筛选">
      <div class="status-tabs">
        <button
          v-for="tab in statusTabs"
          :key="tab.value || 'ALL'"
          type="button"
          class="status-tab"
          :class="{ 'status-tab--active': filters.status === tab.value }"
          @click="setStatusFilter(tab.value)"
        >
          <span>{{ tab.label }}</span>
        </button>
      </div>
      <label class="filter-control">
        <span class="sr-only">反馈类型</span>
        <select v-model="filters.category" @change="reload">
          <option value="">全部类型</option>
          <option v-for="item in categories" :key="item" :value="item">{{ item }}</option>
        </select>
      </label>
      <label class="filter-search">
        <input
          v-model.trim="filters.keyword"
          type="search"
          placeholder="搜索内容、联系方式、昵称或反馈编号"
          @keyup.enter="reload"
        />
      </label>
      <button class="ghost-button" type="button" @click="reload">搜索</button>
      <button class="refresh-button" type="button" :disabled="loading" @click="reload">
        {{ loading ? "刷新中" : "刷新" }}
      </button>
    </section>

    <section class="feedback-workbench">
      <aside class="queue-panel" aria-label="反馈队列">
        <div class="queue-panel__head">
          <div>
            <div class="queue-panel__title">反馈队列</div>
            <div class="queue-panel__desc">待处理优先，按提交时间倒序</div>
          </div>
          <span class="queue-panel__count">{{ total }}</span>
        </div>

        <div v-if="loading" class="queue-state">正在加载反馈…</div>

        <div v-else-if="rows.length" class="queue-list">
          <button
            v-for="item in rows"
            :key="String(item.id)"
            type="button"
            class="queue-item"
            :class="{ 'queue-item--active': current && current.id === item.id }"
            @click="selectRow(item)"
          >
            <span class="queue-item__avatar">
              <img v-if="item.avatarUrl" :src="resolveFileUrl(item.avatarUrl)" alt="" />
              <span v-else>{{ (item.nickname || "用").slice(0, 1) }}</span>
            </span>
            <span class="queue-item__content">
              <span class="queue-item__topline">
                <strong class="queue-item__name">{{ item.nickname || "未设置昵称" }}</strong>
                <span class="status-pill" :class="statusClass(item.status)">{{ statusLabel(item.status) }}</span>
              </span>
              <span class="queue-item__category">{{ item.category }}</span>
              <span class="queue-item__content-text">{{ summarize(item.content) }}</span>
              <span class="queue-item__bottomline">
                <span>#{{ item.id }}</span>
                <span>{{ formatDateTime(item.createdAt) }}</span>
              </span>
            </span>
          </button>
        </div>

        <div v-else class="queue-empty">
          <div class="queue-empty__mark">0</div>
          <strong>没有匹配的反馈</strong>
          <span>调整筛选条件后再试。</span>
        </div>

        <div v-if="total > pageSize" class="pager">
          <button class="ghost-button" type="button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button>
          <span class="pager-text">第 {{ page }} / {{ totalPages }} 页</span>
          <button class="ghost-button" type="button" :disabled="page >= totalPages" @click="changePage(page + 1)">下一页</button>
        </div>
      </aside>

      <article
        v-if="current"
        class="feedback-detail"
        :class="{ 'feedback-detail--mobile-open': mobileDetailOpen }"
      >
        <header class="detail-header">
          <button class="mobile-detail-back" type="button" @click="closeMobileDetail">‹ 返回反馈列表</button>
          <div class="detail-header__account">
            <div class="detail-avatar">
              <img v-if="current.avatarUrl" :src="resolveFileUrl(current.avatarUrl)" alt="" />
              <span v-else>{{ (current.nickname || "用").slice(0, 1) }}</span>
            </div>
            <div>
              <div class="detail-eyebrow">反馈 #{{ current.id }} · UID {{ current.userId }}</div>
              <div class="detail-title-row">
                <h2>{{ current.nickname || "未设置昵称" }}</h2>
                <span class="status-pill" :class="statusClass(current.status)">{{ statusLabel(current.status) }}</span>
                <span class="category-pill">{{ current.category }}</span>
              </div>
              <div class="detail-openid">OpenID {{ current.openId || "-" }}</div>
            </div>
          </div>
          <div class="detail-header__meta">
            <span>提交于 {{ formatDateTime(current.createdAt) }}</span>
            <span v-if="current.handledAt">
              处理于 {{ formatDateTime(current.handledAt) }} · {{ current.handledBy || "-" }}
            </span>
          </div>
        </header>

        <div class="detail-scroll">
          <section class="detail-section">
            <div class="section-heading">
              <div>
                <h3>反馈内容</h3>
                <p>用户在小程序里填写的内容</p>
              </div>
            </div>
            <div class="content-box">{{ current.content }}</div>
          </section>

          <section v-if="current.attachmentUrls && current.attachmentUrls.length" class="detail-section">
            <div class="section-heading">
              <div>
                <h3>附件</h3>
                <p>点击图片可放大查看</p>
              </div>
            </div>
            <div class="attachment-grid">
              <template v-for="(url, index) in current.attachmentUrls">
                <img
                  v-if="isImage(url)"
                  :key="'img-' + index"
                  class="attachment-thumb"
                  :src="resolveFileUrl(url)"
                  alt=""
                  @click="previewImage = resolveFileUrl(url)"
                />
                <a
                  v-else
                  :key="'file-' + index"
                  class="attachment-file"
                  :href="resolveFileUrl(url)"
                  target="_blank"
                  rel="noopener"
                >附件 {{ index + 1 }}（视频/文件）</a>
              </template>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-heading">
              <div>
                <h3>用户与环境信息</h3>
                <p>便于复现问题时参考</p>
              </div>
            </div>
            <dl class="info-grid">
              <div>
                <dt>联系方式</dt>
                <dd>{{ current.contact || "—" }}</dd>
              </div>
              <div>
                <dt>App 版本</dt>
                <dd>{{ current.appVersion || "—" }}</dd>
              </div>
              <div class="info-grid__wide">
                <dt>设备信息</dt>
                <dd>{{ current.clientInfo || "—" }}</dd>
              </div>
              <div class="info-grid__wide" v-if="current.handleRemark">
                <dt>上次处理备注</dt>
                <dd>{{ current.handleRemark }}</dd>
              </div>
            </dl>
          </section>
        </div>

        <footer class="decision-bar">
          <div class="decision-fields">
            <label class="decision-field">
              <span>处理备注 <small>会一并告诉用户（可选）</small></span>
              <textarea v-model.trim="handlingForm.remark" placeholder="例如：已在最新版本修复，请更新后重试" />
            </label>
            <label class="notify-toggle">
              <input v-model="handlingForm.notifyUser" type="checkbox" />
              <span>处理完成后通知用户（站内消息；已关注并绑定服务号的会同步推送服务号提醒）</span>
            </label>
          </div>
          <div class="decision-actions">
            <span v-if="actionMessage" class="action-message" :class="{ 'action-message--error': actionError }">
              {{ actionMessage }}
            </span>
            <button
              class="decision-button"
              type="button"
              :disabled="submitting || current.status === 'PROCESSING'"
              @click="updateStatus('PROCESSING')"
            >标记处理中</button>
            <button
              class="decision-button"
              type="button"
              :disabled="submitting || current.status === 'IGNORED'"
              @click="updateStatus('IGNORED')"
            >忽略</button>
            <button
              class="decision-button decision-button--primary"
              type="button"
              :disabled="submitting || current.status === 'RESOLVED'"
              @click="updateStatus('RESOLVED')"
            >标记已处理</button>
          </div>
        </footer>
      </article>

      <div v-else class="feedback-detail feedback-detail--empty">
        <div class="detail-empty__mark">馈</div>
        <h2>选择一条反馈查看详情</h2>
        <p>左侧队列会保留当前筛选条件。</p>
      </div>
    </section>

    <div v-if="previewImage" class="image-preview" @click="previewImage = ''">
      <img :src="previewImage" alt="图片预览" />
      <span class="image-preview__hint">点击任意位置关闭</span>
    </div>
  </div>
</template>

<script>
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"
import { fetchFeedbackList, fetchFeedbackStats, updateFeedbackHandling } from "../api/feedback"

function emptyStats() {
  return { total: 0, pending: 0, processing: 0, resolved: 0, ignored: 0, todaySubmitted: 0 }
}

export default {
  name: "FeedbackManageView",
  data() {
    return {
      loading: false,
      submitting: false,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      stats: emptyStats(),
      current: null,
      mobileDetailOpen: false,
      previewImage: "",
      actionMessage: "",
      actionError: false,
      categories: ["功能异常", "使用建议", "数据问题", "其他"],
      filters: { status: "PENDING", category: "", keyword: "" },
      handlingForm: { remark: "", notifyUser: true }
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.total || 0) / this.pageSize))
    },
    statusTabs() {
      return [
        { label: "待处理", value: "PENDING" },
        { label: "处理中", value: "PROCESSING" },
        { label: "已处理", value: "RESOLVED" },
        { label: "已忽略", value: "IGNORED" },
        { label: "全部", value: "" }
      ]
    }
  },
  created() {
    this.reload()
  },
  methods: {
    resolveFileUrl(value) {
      return resolveBackendAssetUrl(value)
    },
    isImage(value) {
      return !!value && /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(value)
    },
    summarize(text) {
      const content = String(text || "")
      return content.length > 40 ? content.slice(0, 40) + "…" : content
    },
    statusLabel(status) {
      if (status === "PROCESSING") return "处理中"
      if (status === "RESOLVED") return "已处理"
      if (status === "IGNORED") return "已忽略"
      return "待处理"
    },
    statusClass(status) {
      if (status === "PROCESSING") return "status-pill--processing"
      if (status === "RESOLVED") return "status-pill--resolved"
      if (status === "IGNORED") return "status-pill--ignored"
      return "status-pill--pending"
    },
    formatDateTime(value) {
      if (!value) return "-"
      return String(value).replace("T", " ").slice(0, 16)
    },
    async reload() {
      this.loading = true
      try {
        const [listRes, statsRes] = await Promise.all([
          fetchFeedbackList({
            status: this.filters.status || undefined,
            category: this.filters.category || undefined,
            keyword: this.filters.keyword || undefined,
            page: this.page,
            pageSize: this.pageSize
          }),
          fetchFeedbackStats()
        ])
        const data = listRes.data || {}
        this.rows = Array.isArray(data.records) ? data.records : []
        this.total = Number(data.total || 0)
        const stats = statsRes.data || {}
        this.stats = {
          total: Number(stats.total || 0),
          pending: Number(stats.pending || 0),
          processing: Number(stats.processing || 0),
          resolved: Number(stats.resolved || 0),
          ignored: Number(stats.ignored || 0),
          todaySubmitted: Number(stats.todaySubmitted || 0)
        }
        const stillVisible = this.rows.some(item => this.current && item.id === this.current.id)
        if (!stillVisible) {
          this.current = this.rows.length ? this.rows[0] : null
          if (!this.current) this.mobileDetailOpen = false
          this.resetHandlingForm()
        }
      } catch (error) {
        this.rows = []
        this.current = null
        this.mobileDetailOpen = false
        this.actionMessage = (error && error.message) || "反馈加载失败"
        this.actionError = true
      } finally {
        this.loading = false
      }
    },
    setStatusFilter(status) {
      this.filters.status = status
      this.mobileDetailOpen = false
      this.page = 1
      this.reload()
    },
    changePage(next) {
      this.page = Math.min(Math.max(1, next), this.totalPages)
      this.reload()
    },
    selectRow(item) {
      this.current = item
      this.mobileDetailOpen = window.matchMedia("(max-width: 760px)").matches
      this.resetHandlingForm()
      this.actionMessage = ""
      this.actionError = false
    },
    closeMobileDetail() {
      this.mobileDetailOpen = false
    },
    resetHandlingForm() {
      this.handlingForm = { remark: "", notifyUser: true }
    },
    async updateStatus(status) {
      if (!this.current || this.submitting) return
      if (status !== "PROCESSING" && !this.handlingForm.remark) {
        if (!window.confirm("没有填写处理备注，确认继续？备注会作为回复内容发给用户。")) return
      }
      this.submitting = true
      this.actionMessage = ""
      this.actionError = false
      try {
        const currentId = this.current.id
        const res = await updateFeedbackHandling(currentId, {
          status,
          remark: this.handlingForm.remark || undefined,
          notifyUser: this.handlingForm.notifyUser
        })
        const data = res.data || {}
        let message = `已更新为「${this.statusLabel(status)}」`
        if (data.officialNotifyMessage) {
          message += ` · ${data.officialNotifyMessage}`
        }
        this.actionMessage = message
        this.actionError = data.officialNotifyStatus === "FAILED"
        await this.reload()
        const next = this.rows.find(item => item.id === currentId)
        this.current = next || (this.rows.length ? this.rows[0] : null)
        this.resetHandlingForm()
      } catch (error) {
        this.actionMessage = (error && error.message) || "操作失败，请稍后重试"
        this.actionError = true
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.feedback-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-height: 100%;
  color: #17221d;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.feedback-toolbar,
.feedback-filters {
  padding: 18px 22px;
  background: #fff;
  border: 1px solid #e5ebe7;
  border-radius: 16px;
  box-shadow: 0 10px 26px rgba(23, 34, 29, 0.05);
}

.feedback-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  padding: 14px 16px;
}

.feedback-toolbar__title {
  font-size: 20px;
  font-weight: 700;
}

.feedback-toolbar__desc {
  margin-top: 6px;
  max-width: 620px;
  color: #6c7a73;
  font-size: 12.5px;
  line-height: 1.7;
}

.feedback-metrics {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.feedback-metric {
  min-width: 84px;
  padding: 10px 14px;
  text-align: center;
  background: #f5f8f6;
  border-radius: 12px;
}

.feedback-metric span {
  display: block;
  color: #7a8781;
  font-size: 12px;
}

.feedback-metric strong {
  display: block;
  margin-top: 4px;
  font-size: 20px;
  font-weight: 700;
  color: #1f2d26;
}

.feedback-metric--pending strong {
  color: #d08700;
}

.feedback-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.status-tabs {
  display: flex;
  gap: 6px;
  padding: 4px;
  background: #f2f5f3;
  border-radius: 12px;
}

.status-tab {
  padding: 8px 14px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #5d6b64;
  font-size: 13px;
  cursor: pointer;
}

.status-tab--active {
  background: #fff;
  color: #12301f;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(23, 34, 29, 0.08);
}

.filter-control select,
.filter-search input {
  height: 36px;
  padding: 0 12px;
  border: 1px solid #dfe6e1;
  border-radius: 10px;
  background: #fff;
  color: #1f2d26;
  font-size: 13px;
  outline: none;
}

.filter-search input {
  width: 260px;
}

.ghost-button,
.refresh-button {
  height: 36px;
  padding: 0 16px;
  border-radius: 10px;
  font-size: 13px;
  cursor: pointer;
}

.ghost-button {
  border: 1px solid #dfe6e1;
  background: #fff;
  color: #33463b;
}

.refresh-button {
  border: 0;
  background: #0fbd68;
  color: #fff;
}

.refresh-button:disabled,
.ghost-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.feedback-workbench {
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.queue-panel,
.feedback-detail {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e5ebe7;
  border-radius: 16px;
  box-shadow: 0 10px 26px rgba(23, 34, 29, 0.05);
}

.queue-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid #eef2ef;
}

.queue-panel__title {
  font-size: 15px;
  font-weight: 600;
}

.queue-panel__desc {
  margin-top: 4px;
  color: #86928c;
  font-size: 12px;
}

.queue-panel__count {
  padding: 4px 10px;
  background: #f2f5f3;
  border-radius: 999px;
  color: #4d5c54;
  font-size: 12px;
}

.queue-list {
  max-height: 62vh;
  overflow-y: auto;
  padding: 8px;
}

.queue-item {
  display: flex;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.queue-item:hover {
  background: #f7faf8;
}

.queue-item--active {
  background: #eefaf2;
}

.queue-item__avatar,
.detail-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 12px;
  overflow: hidden;
  background: #e8f5ec;
  color: #1f7a49;
  font-weight: 600;
}

.queue-item__avatar img,
.detail-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.queue-item__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.queue-item__topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.queue-item__name {
  font-size: 14px;
  font-weight: 600;
}

.queue-item__category {
  color: #2f5a45;
  font-size: 12px;
}

.queue-item__content-text {
  color: #6f7d76;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.queue-item__bottomline {
  display: flex;
  justify-content: space-between;
  color: #97a29c;
  font-size: 12px;
}

.queue-state,
.queue-empty {
  padding: 40px 20px;
  text-align: center;
  color: #86928c;
  font-size: 13px;
}

.queue-empty {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.queue-empty__mark {
  font-size: 26px;
  font-weight: 700;
  color: #cdd8d2;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid #eef2ef;
}

.pager-text {
  color: #86928c;
  font-size: 12px;
}

.status-pill {
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
}

.status-pill--pending {
  background: #fff6e2;
  color: #a86f00;
}

.status-pill--processing {
  background: #eaf2fd;
  color: #2b5fa8;
}

.status-pill--resolved {
  background: #e8f8ef;
  color: #14794a;
}

.status-pill--ignored {
  background: #f2f3f3;
  color: #6d7a74;
}

.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  border-bottom: 1px solid #eef2ef;
  flex-wrap: wrap;
}

.mobile-detail-back {
  display: none;
}

.detail-header__account {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.detail-eyebrow {
  color: #86928c;
  font-size: 12px;
}

.detail-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
  flex-wrap: wrap;
}

.detail-title-row h2 {
  margin: 0;
  font-size: 20px;
}

.category-pill {
  padding: 3px 10px;
  border-radius: 999px;
  background: #eef5f1;
  color: #2f5a45;
  font-size: 12px;
}

.detail-openid {
  margin-top: 8px;
  color: #97a29c;
  font-size: 12px;
  word-break: break-all;
}

.detail-header__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #86928c;
  font-size: 12px;
  text-align: right;
}

.detail-scroll {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 20px 22px;
}

.detail-section {
  padding: 16px 18px;
  border: 1px solid #eef2ef;
  border-radius: 14px;
}

.section-heading h3 {
  margin: 0;
  font-size: 15px;
}

.section-heading p {
  margin: 4px 0 0;
  color: #86928c;
  font-size: 12px;
}

.content-box {
  margin-top: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #f7faf8;
  color: #33463b;
  font-size: 13.5px;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
}

.attachment-grid {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.attachment-thumb {
  width: 104px;
  height: 104px;
  object-fit: cover;
  border-radius: 10px;
  background: #f2f5f3;
  cursor: zoom-in;
}

.attachment-file {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #dfe6e1;
  border-radius: 10px;
  color: #2f5a45;
  font-size: 12.5px;
  text-decoration: none;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px 18px;
  margin: 12px 0 0;
}

.info-grid__wide {
  grid-column: 1 / -1;
}

.info-grid dt {
  color: #86928c;
  font-size: 12px;
}

.info-grid dd {
  margin: 5px 0 0;
  color: #1f2d26;
  font-size: 13.5px;
  word-break: break-all;
}

.decision-bar {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 22px;
  border-top: 1px solid #eef2ef;
  position: sticky;
  bottom: 0;
  z-index: 3;
  background: #fff;
  border-radius: 0 0 16px 16px;
}

.decision-fields {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.decision-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 320px;
  font-size: 13px;
  color: #33463b;
}

.decision-field small {
  color: #97a29c;
  font-size: 12px;
}

.decision-field textarea {
  min-height: 64px;
  padding: 10px 12px;
  border: 1px solid #dfe6e1;
  border-radius: 10px;
  background: #fff;
  font-size: 13px;
  line-height: 1.6;
  resize: vertical;
  outline: none;
}

.notify-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4d5c54;
  font-size: 13px;
}

.decision-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.action-message {
  margin-right: auto;
  color: #14794a;
  font-size: 13px;
}

.action-message--error {
  color: #b23f2c;
}

.decision-button {
  height: 40px;
  padding: 0 20px;
  border: 1px solid #dfe6e1;
  border-radius: 10px;
  background: #fff;
  color: #33463b;
  font-size: 13.5px;
  cursor: pointer;
}

.decision-button--primary {
  border: 0;
  background: #0fbd68;
  color: #fff;
}

.decision-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.feedback-detail--empty {
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 80px 20px;
  color: #86928c;
  text-align: center;
}

.detail-empty__mark {
  font-size: 30px;
  font-weight: 700;
  color: #cdd8d2;
}

.feedback-detail--empty h2 {
  margin: 0;
  color: #33463b;
  font-size: 17px;
}

.feedback-detail--empty p {
  margin: 0;
  font-size: 13px;
}

.image-preview {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px;
  background: rgba(12, 22, 17, 0.82);
  cursor: zoom-out;
}

.image-preview img {
  max-width: 92vw;
  max-height: 84vh;
  object-fit: contain;
  border-radius: 12px;
  background: #fff;
}

.image-preview__hint {
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
}

@media (max-width: 1180px) {
  .feedback-workbench {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .feedback-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    width: 100%;
  }

  .feedback-metric {
    min-width: 0;
    padding: 10px 6px;
  }

  #app .feedback-filters {
    display: grid !important;
    grid-template-columns: 1fr 1fr;
    align-items: stretch;
    gap: 8px;
    padding: 12px;
  }

  .feedback-filters .status-tabs,
  .feedback-filters .filter-control,
  .feedback-filters .filter-search {
    grid-column: 1 / -1;
  }

  .feedback-filters .filter-search input {
    width: 100%;
  }

  .feedback-filters .ghost-button,
  .feedback-filters .refresh-button {
    width: 100%;
    height: 38px;
  }

  .queue-list {
    max-height: none;
    overflow: visible;
  }

  .queue-panel {
    min-height: 220px;
  }

  .feedback-detail,
  .feedback-detail--empty {
    display: none;
  }

  .feedback-detail--mobile-open {
    position: fixed;
    inset: 0;
    z-index: 90;
    display: flex;
    max-height: 100dvh;
    overflow-y: auto;
    border: 0;
    border-radius: 0;
    background: #f5f7f6;
    box-shadow: none;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  .feedback-detail--mobile-open .detail-header {
    position: sticky;
    top: 0;
    z-index: 4;
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
    padding: calc(10px + env(safe-area-inset-top)) 14px 14px;
    background: #ffffff;
  }

  .mobile-detail-back {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    min-height: 34px;
    padding: 0 4px;
    border: 0;
    background: transparent;
    color: #246347;
    font-size: 13px;
    font-weight: 600;
  }

  .detail-header__account {
    align-items: flex-start;
    width: 100%;
  }

  .detail-title-row h2 {
    font-size: 18px;
  }

  .detail-header__meta {
    align-items: flex-start;
    text-align: left;
  }

  .detail-scroll {
    gap: 12px;
    padding: 12px;
  }

  .detail-section {
    padding: 14px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .decision-bar {
    position: sticky;
    bottom: 0;
    padding: 12px 12px calc(12px + env(safe-area-inset-bottom));
    border-radius: 0;
    box-shadow: 0 -8px 24px rgba(23, 34, 29, 0.08);
  }

  .decision-field {
    flex-basis: 100%;
  }

  .decision-actions {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    width: 100%;
  }

  .decision-button {
    width: 100%;
    padding: 0 8px;
  }

  .action-message {
    grid-column: 1 / -1;
  }
}
</style>
